import categories from '@data/cats';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import styled, { css } from 'styled-components';
import LanguageSwitcher from '@/components/language-switcher';
import OutsideNorthAmerica from '@/components/outside-north-america';
import SocialMediaProfiles from '@/components/social-media-profiles';
import { Logo, SectionContainer, VisuallyHidden } from '@/components/ui';
import config from '@/config';
import { COOKIE_SETTINGS_EVENT } from '@/lib/consent';
import { screens, up } from '@/themes';

const MARKETING_URL = 'https://www.bidali.com';
const APP_DOWNLOAD_URL = `https://www.bidali.com/app?utm_campaign=${config.appDownloadCampaign}&utm_medium=giftcards_site&utm_source=giftcard_site&utm_content=app_download_cta_footer`;

/*
 * Every category has a link under "Shop", grouped by theme: food and drink,
 * shopping and home, gifts, entertainment and sport, travel. The list reads
 * down the first column, then the second. A category the catalogue adds later
 * is appended after these, in catalogue order.
 */
const SHOP_ORDER = [
  'restaurants-and-food',
  'groceries',
  'coffee',
  'wine-and-cocktails',
  'clothing-and-accessories',
  'house-and-home',
  'renovations',
  'maternity-and-toys',
  'health-and-spa',
  'gifts-for-her',
  'gifts-for-him',
  'entertainment',
  'gaming',
  'sports',
  'golf',
  'travel',
  'flights',
  'hotels',
  'uber',
];

const shopRank = (slug) => {
  const index = SHOP_ORDER.indexOf(slug);
  return index === -1 ? SHOP_ORDER.length : index;
};

/** Every category of the catalogue (data/cats.json), in the order the footer lists them. */
export const shopCategories = (all = categories) =>
  all
    .filter((category) => category?.slug)
    .map(({ id, name, slug }) => ({ id, name, slug }))
    // Array.prototype.sort is stable: unknown slugs keep the catalogue's order.
    .sort((a, b) => shopRank(a.slug) - shopRank(b.slug));

const SHOP_CATEGORIES = shopCategories();

const COMPANY_LINKS = [
  { key: 'about', href: `${MARKETING_URL}/company` },
  // The marketing site has no /products index; its home page lists the products.
  { key: 'products', href: MARKETING_URL },
  { key: 'pricing', href: `${MARKETING_URL}/pricing` },
  { key: 'caseStudies', href: `${MARKETING_URL}/case-studies` },
  { key: 'blog', href: `${MARKETING_URL}/blog` },
];

const LEGAL_LINKS = [
  { key: 'termsOfUse', href: `${MARKETING_URL}/legal/terms` },
  { key: 'privacyNotice', href: `${MARKETING_URL}/legal/privacy` },
  { key: 'cookiePolicy', href: `${MARKETING_URL}/legal/cookies` },
  { key: 'restrictedUse', href: `${MARKETING_URL}/legal/restricted-use` },
  { key: 'rewardsTerms', href: `${MARKETING_URL}/legal/cashback` },
  { key: 'complianceProgram', href: `${MARKETING_URL}/legal/aml` },
  { key: 'allPolicies', href: `${MARKETING_URL}/legal` },
];

/*
 * Handbook "General Disclaimer", copied verbatim from the marketing site
 * (src/content/navigation.ts, navigation.footer.disclaimer). English only:
 * do not translate or edit; change it there first.
 */
const DISCLAIMER = [
  [
    'Bidali Inc. is a software company and an independent reseller of gift cards, prepaid cards and other prepaid products made available for purchase within the Bidali platform (the "Services"). Bidali is not a bank or regulated financial institution, does not provide payment processing or money transmission, and does not hold your funds in the manner of a bank account. Bidali One amounts are prepaid platform credit that can only be spent on the Services; they are not a deposit, loan or investment.',
  ],
  [
    'The brands whose gift cards and products Bidali sells, and their owners and affiliates (each a "Brand"), do not sponsor or endorse Bidali or the Services and have no relationship with Bidali beyond a reseller or affiliate marketing arrangement. Brand names, logos, trademarks and other identifying marks belong to their respective Brands. For complete terms, see Bidali’s ',
    { label: 'Terms of Use', href: `${MARKETING_URL}/legal/terms` },
    ' and each Brand’s own product terms, shown with the product details at ',
    { label: 'giftcards.bidali.com', href: 'https://giftcards.bidali.com' },
    '.',
  ],
  [
    'Content on this site is for information only. Nothing on it is endorsed or sponsored by any Brand or third-party platform unless stated, and nothing on it is investment, tax, financial or legal advice.',
  ],
];

const noise =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.5 0 0 0 0 0.5 0 0 0 0 0.5 0 0 0 1 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

const bg = (percent) => `color-mix(in oklab, var(--background) ${percent}%, transparent)`;

const Container = styled.footer`
  background-image: var(--gradient-soft);
  color: var(--foreground);
  isolation: isolate;
  padding-top: 48px;
  position: relative;

  ${up.sm} {
    padding-top: 80px;
  }

  /* Faint noise dithers the low-contrast gradient so it does not band. */
  &::before {
    background-image: ${noise};
    background-size: 160px 160px;
    content: '';
    inset: 0;
    mix-blend-mode: overlay;
    opacity: 0.06;
    pointer-events: none;
    position: absolute;
    z-index: -1;
  }
`;

// Edge to edge on phones, where an inset panel leaves too narrow a gutter.
const Glass = styled.div`
  -webkit-backdrop-filter: blur(24px);
  backdrop-filter: blur(24px);
  background-image: linear-gradient(to bottom, ${bg(25)}, ${bg(50)}, ${bg(80)});
  border-radius: var(--radius-4xl) var(--radius-4xl) 0 0;
  bottom: 0;
  box-shadow:
    inset 0 1px 0 0 var(--primary-foreground),
    0 0 0 1px color-mix(in oklab, var(--border) 70%, transparent);
  left: 0;
  position: absolute;
  right: 0;
  top: 48px;

  ${up.sm} {
    border-radius: var(--radius-4xl);
    bottom: 8px;
    left: 8px;
    right: 8px;
    top: 80px;
  }

  &::before {
    background-image: linear-gradient(
      to right,
      transparent,
      color-mix(in oklab, var(--primary-foreground) 90%, transparent),
      transparent
    );
    content: '';
    height: 1px;
    left: 48px;
    position: absolute;
    right: 48px;
    top: 0;
  }

  .dark & {
    background-image: linear-gradient(to bottom, ${bg(30)}, ${bg(55)}, ${bg(80)});
    box-shadow:
      inset 0 1px 0 0 color-mix(in oklab, var(--primary-foreground) 25%, transparent),
      0 0 0 1px color-mix(in oklab, var(--border) 70%, transparent);

    &::before {
      background-image: linear-gradient(
        to right,
        transparent,
        color-mix(in oklab, var(--primary-foreground) 40%, transparent),
        transparent
      );
    }
  }
`;

const Content = styled(SectionContainer)`
  padding-bottom: 48px;
  position: relative;

  @media (max-width: ${screens.sm - 1}px) {
    padding-left: 24px;
    padding-right: 24px;
  }
`;

const row = css`
  border-top: 1px solid var(--border);
  padding-bottom: 24px;
  padding-top: 24px;
`;

const Sitemap = styled.div`
  display: grid;
  gap: 40px 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding-bottom: 32px;
  padding-top: 40px;

  ${up.lg} {
    gap: 32px;
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`;

const LogoColumn = styled.div`
  grid-column: span 2;

  ${up.lg} {
    grid-column: span 1;
  }
`;

const LogoLink = styled(Link)`
  border-radius: var(--radius-sm);
  display: inline-flex;
`;

const Columns = styled.nav`
  display: grid;
  gap: 40px 24px;
  grid-column: span 2;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  ${up.sm} {
    gap: 48px 32px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  ${up.lg} {
    grid-column: span 5;
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
`;

/*
 * Shop lists every category, so it takes two of the five columns and sets its
 * links in two columns of its own. Below 1024px it takes the full width (three
 * link columns from 640px, two on phones).
 */
const ShopColumn = styled.div`
  grid-column: 1 / -1;

  ${up.lg} {
    grid-column: span 2;
  }
`;

/*
 * Help and Partners are short: from 640px they share one column, which is what
 * makes room for Shop. On phones they stay two cells of the two-column grid.
 */
const Stack = styled.div`
  display: contents;

  ${up.sm} {
    align-content: start;
    display: grid;
    gap: 32px;
  }
`;

const ColumnTitle = styled.h3`
  color: var(--foreground);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  line-height: 24px;
  margin: 0;
  text-transform: uppercase;
`;

const LinkList = styled.ul`
  display: flex;
  flex-direction: column;
  font-size: 14px;
  gap: 8px;
  line-height: 24px;
  list-style: none;
  margin: 16px 0 0;
  padding: 0;
`;

/* Same rhythm as LinkList (8px between links), in CSS columns that fill top to bottom. */
const ShopList = styled(LinkList)`
  column-gap: 24px;
  columns: 2;
  display: block;
  margin-bottom: -8px;

  ${up.sm} {
    column-gap: 32px;
    columns: 3;
  }

  ${up.lg} {
    columns: 2;
  }

  li {
    break-inside: avoid;
    padding-bottom: 8px;
  }
`;

const linkStyle = css`
  appearance: none;
  background: none;
  border: 0;
  border-radius: var(--radius-sm);
  color: color-mix(in oklab, var(--foreground) 80%, transparent);
  cursor: pointer;
  display: inline-block;
  font-family: inherit;
  font-size: inherit;
  font-weight: 500;
  line-height: inherit;
  margin: 0;
  overflow-wrap: anywhere;
  padding: 4px 0;
  text-align: left;
  text-decoration: none;
  transition: color 150ms;

  &:hover {
    color: var(--foreground);
  }
`;

const FooterLink = styled(Link)`
  ${linkStyle};
`;

const ExternalLink = styled.a`
  ${linkStyle};
`;

const LinkButton = styled.button`
  ${linkStyle};
`;

const LegalRow = styled.div`
  ${row};
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${up.lg} {
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
  }
`;

const LegalList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  font-size: 14px;
  gap: 4px 20px;
  line-height: 24px;
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Language = styled(LanguageSwitcher)`
  align-self: flex-start;
  margin-left: -12px;

  ${up.lg} {
    align-self: auto;
    flex-shrink: 0;
    margin-left: 0;
    margin-right: -12px;

    /* Left-aligned below lg, right-aligned once the switcher sits on the right. */
    [role='menu'] {
      left: auto;
      right: 0;
    }
  }
`;

const Disclaimer = styled.div`
  ${row};
  color: var(--muted-foreground);
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-style: italic;
  gap: 12px;
  line-height: 20px;

  p {
    line-height: inherit;
    margin: 0;
  }

  a {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
      color: var(--foreground);
    }
  }
`;

const Bottom = styled.div`
  border-top: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-top: 24px;

  ${up.sm} {
    align-items: flex-end;
    flex-direction: row;
    justify-content: space-between;
  }
`;

const Company = styled.div`
  color: color-mix(in oklab, var(--foreground) 80%, transparent);
  display: flex;
  flex-direction: column;
  font-size: 14px;
  gap: 4px;
  line-height: 24px;

  p,
  address {
    font-style: normal;
    line-height: inherit;
    margin: 0;
  }

  a {
    color: inherit;
    white-space: nowrap;

    &:hover {
      color: var(--foreground);
    }
  }
`;

const external = { rel: 'noopener noreferrer', target: '_blank' };

const Footer = () => {
  const t = useTranslations('footer');
  const nT = useTranslations('nav');
  const cT = useTranslations('categories');

  const openCookieSettings = () => {
    window.dispatchEvent(new CustomEvent(COOKIE_SETTINGS_EVENT));
  };

  return (
    <Container aria-labelledby="footer-heading" data-section="footer">
      <VisuallyHidden as="h2" id="footer-heading">
        {t('heading')}
      </VisuallyHidden>
      <Glass aria-hidden="true" />
      <Content>
        <Sitemap>
          <LogoColumn>
            <LogoLink href="/" aria-label={nT('home')}>
              <Logo height={28} />
            </LogoLink>
          </LogoColumn>
          <Columns aria-label={t('heading')}>
            <ShopColumn>
              <ColumnTitle>{t('shop')}</ColumnTitle>
              <ShopList>
                {SHOP_CATEGORIES.map((category) => (
                  <li key={category.id}>
                    <FooterLink href={`/shop/${category.slug}`}>
                      {/* A category newer than the translations shows the catalogue's name. */}
                      {cT.has(category.slug) ? cT(category.slug) : category.name}
                    </FooterLink>
                  </li>
                ))}
                <li>
                  <FooterLink href="/buy">{t('allGiftCards')}</FooterLink>
                </li>
              </ShopList>
            </ShopColumn>
            <div>
              <ColumnTitle>{t('browse')}</ColumnTitle>
              <LinkList>
                <li>
                  <FooterLink href="/countries">{t('allCountries')}</FooterLink>
                </li>
                <li>
                  <FooterLink href="/currency">{t('byCurrency')}</FooterLink>
                </li>
                <li>
                  <FooterLink href="/networks">{t('networks')}</FooterLink>
                </li>
                <li>
                  <FooterLink href="/agents">{t('agents')}</FooterLink>
                </li>
                <li>
                  <FooterLink href="/developers">{t('developers')}</FooterLink>
                </li>
                {/* Compliance: these two must never reach server HTML or North American visitors. */}
                <OutsideNorthAmerica>
                  <li>
                    <FooterLink href="/currencies">{t('rates')}</FooterLink>
                  </li>
                  <li>
                    <FooterLink href="/stablecoins">{t('waysToPay')}</FooterLink>
                  </li>
                </OutsideNorthAmerica>
                <li>
                  <ExternalLink href={APP_DOWNLOAD_URL} rel="noopener noreferrer">
                    {t('downloadApp')}
                  </ExternalLink>
                </li>
              </LinkList>
            </div>
            <Stack>
              <div>
                <ColumnTitle>{t('help')}</ColumnTitle>
                <LinkList>
                  <li>
                    <ExternalLink href={config.supportUrl} {...external}>
                      {t('support')}
                    </ExternalLink>
                  </li>
                  <li>
                    <FooterLink href="/contact">{t('contact')}</FooterLink>
                  </li>
                  <li>
                    <ExternalLink href={config.brandRequestUrl} {...external}>
                      {t('requestBrand')}
                    </ExternalLink>
                  </li>
                </LinkList>
              </div>
              <div>
                <ColumnTitle>{t('partners')}</ColumnTitle>
                <LinkList>
                  <li>
                    <ExternalLink href={config.addYourBusinessUrl} {...external}>
                      {t('addBusiness')}
                    </ExternalLink>
                  </li>
                  <li>
                    <ExternalLink href={config.integrateBidaliUrl} {...external}>
                      {t('integrate')}
                    </ExternalLink>
                  </li>
                </LinkList>
              </div>
            </Stack>
            <div>
              <ColumnTitle>{t('company')}</ColumnTitle>
              <LinkList>
                {COMPANY_LINKS.map((link) => (
                  <li key={link.key}>
                    <ExternalLink href={link.href}>{t(link.key)}</ExternalLink>
                  </li>
                ))}
              </LinkList>
            </div>
          </Columns>
        </Sitemap>

        <LegalRow>
          <LegalList aria-label={t('legal')}>
            {LEGAL_LINKS.map((link) => (
              <li key={link.key}>
                <ExternalLink href={link.href} {...external}>
                  {t(link.key)}
                </ExternalLink>
              </li>
            ))}
            <li>
              <LinkButton type="button" onClick={openCookieSettings}>
                {t('cookieSettings')}
              </LinkButton>
            </li>
          </LegalList>
          <Language align="start" showLabel side="top" />
        </LegalRow>

        <Disclaimer lang="en">
          <VisuallyHidden as="h3">{t('disclaimerHeading')}</VisuallyHidden>
          {DISCLAIMER.map((paragraph) => (
            <p
              key={typeof paragraph[0] === 'string' ? paragraph[0].slice(0, 24) : paragraph[0].href}
            >
              {paragraph.map((segment) =>
                typeof segment === 'string' ? (
                  segment
                ) : (
                  <a href={segment.href} key={segment.href}>
                    {segment.label}
                  </a>
                )
              )}
            </p>
          ))}
        </Disclaimer>

        <Bottom>
          <Company>
            <p suppressHydrationWarning>&copy; {new Date().getFullYear()} Bidali Inc.</p>
            <address>
              #300 - 1550 5 St SW, Calgary, AB T2R 1K3, Canada &middot;{' '}
              <a href="tel:+18558864830">+1 (855) 886-4830</a>
            </address>
            <p>
              {t('version')}: v{process.env.NEXT_PUBLIC_APP_VERSION}
            </p>
          </Company>
          <SocialMediaProfiles justifyContent="flex-start" />
        </Bottom>
      </Content>
    </Container>
  );
};

export default Footer;
